import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { simulatorTabs } from "@/lib/api/simulator-tabs";
import { tickerWorkspaces } from "@/lib/api/ticker-workspaces";
import { ApiError } from "@/lib/api/transport";
import { emit } from "@/lib/event-bus";
import { latestPriceFor } from "@/lib/quotes";
import {
  simulatorTabIdForTemplate,
  workspaceStrategyBelongsToTemplate,
} from "@/lib/strategy/library";
import {
  increasesStrategySlotOverflow,
  simulationCapacityOverflowLayers,
  strategySlotCapacityOverflowLayers,
} from "@/lib/strategy/simulation-capacity";
import { deepEqual } from "@/lib/utils";
import type { TickerWorkspace, TickerWorkspaceStrategy } from "@/types/api";
import type { StrategyLayer } from "@/types/strategy";
import type { ScenarioContext, Template } from "@/types/template";
import { useLiveQuotesStore } from "@/stores/liveQuotesStore";
import { syncLayerEnabled, useSimulatorStore } from "@/stores/simulatorStore";
import { useAuth } from "./useAuth";

const api = { ...simulatorTabs, ...tickerWorkspaces };

export type TickerWorkspacesStatus = "idle" | "loading" | "ready" | "error";

export interface TickerWorkspaceImportResult {
  workspace: TickerWorkspace;
  limitReached: boolean;
}

export interface TickerWorkspacesContextValue {
  status: TickerWorkspacesStatus;
  workspaces: readonly TickerWorkspace[];
  activeWorkspaceId: string | null;
  activeWorkspace: TickerWorkspace | null;
  activeTemplate: Template | undefined;
  error: Error | null;
  reload: () => Promise<void>;
  switchWorkspace: (workspaceId: string) => void;
  ensureWorkspace: (ticker: string) => Promise<TickerWorkspace>;
  archiveWorkspace: (workspaceId: string) => Promise<void>;
  importTemplate: (template: Template) => Promise<TickerWorkspaceImportResult>;
  updateWorkspaceState: (
    workspaceId: string,
    patch: Record<string, unknown>,
  ) => Promise<void>;
  replaceWorkspaceLayers: (
    workspaceId: string,
    layers: StrategyLayer[],
  ) => Promise<void>;
  saveWorkspaceLayer: (
    workspaceId: string,
    layer: StrategyLayer,
  ) => Promise<void>;
  removeWorkspaceLayer: (workspaceId: string, layerId: string) => Promise<void>;
  removeWorkspaceLayerWithHistory: (
    workspaceId: string,
    layerId: string,
  ) => Promise<void>;
}

const TickerWorkspacesContext =
  createContext<TickerWorkspacesContextValue | null>(null);

function sortWorkspaces(
  workspaces: readonly TickerWorkspace[],
): TickerWorkspace[] {
  return [...workspaces].sort(
    (a, b) =>
      a.position - b.position ||
      a.created_at.localeCompare(b.created_at) ||
      a.id.localeCompare(b.id),
  );
}

function strategyLayer(strategy: TickerWorkspaceStrategy): StrategyLayer {
  return {
    ...strategy.layer,
    params: { ...strategy.layer.params },
    enabled: strategy.enabled,
  };
}

function optimisticWorkspaceStrategies(
  workspace: TickerWorkspace,
  layers: readonly StrategyLayer[],
): TickerWorkspaceStrategy[] {
  const existingByLayerId = new Map(
    workspace.strategies.map((strategy) => [strategy.layer.id, strategy]),
  );
  const now = new Date().toISOString();
  return layers.map((layer, position) => {
    const existing = existingByLayerId.get(layer.id);
    if (existing) {
      return {
        ...existing,
        layer: { ...layer, params: { ...layer.params } },
        enabled: layer.enabled,
        position,
      };
    }
    return {
      id: `pending:${workspace.id}:${layer.id}`,
      workspace_id: workspace.id,
      simulator_tab_id: null,
      saved_strategy_id: null,
      source_layer_id: null,
      layer: { ...layer, params: { ...layer.params } },
      enabled: layer.enabled,
      position,
      created_at: now,
      updated_at: now,
    };
  });
}

function rebaseWorkspaceLayers(
  baseStrategies: readonly TickerWorkspaceStrategy[],
  desiredLayers: readonly StrategyLayer[],
  canonicalStrategies: readonly TickerWorkspaceStrategy[],
): StrategyLayer[] {
  const baseByLayerId = new Map(
    baseStrategies.map((strategy) => [
      strategy.layer.id,
      strategyLayer(strategy),
    ]),
  );
  const desiredByLayerId = new Map(
    desiredLayers.map((layer) => [layer.id, layer]),
  );
  const removedLayerIds = new Set(
    baseStrategies
      .map((strategy) => strategy.layer.id)
      .filter((layerId) => !desiredByLayerId.has(layerId)),
  );
  const locallyChangedLayerIds = new Set(
    desiredLayers
      .filter((layer) => {
        const base = baseByLayerId.get(layer.id);
        return !base || !deepEqual(base, layer);
      })
      .map((layer) => layer.id),
  );

  const mergedByLayerId = new Map<string, StrategyLayer>();
  for (const strategy of canonicalStrategies) {
    const layerId = strategy.layer.id;
    if (removedLayerIds.has(layerId)) continue;
    const local = desiredByLayerId.get(layerId);
    mergedByLayerId.set(
      layerId,
      local && locallyChangedLayerIds.has(layerId)
        ? local
        : strategyLayer(strategy),
    );
  }
  for (const layer of desiredLayers) {
    if (locallyChangedLayerIds.has(layer.id)) {
      mergedByLayerId.set(layer.id, layer);
    }
  }

  const ordered: StrategyLayer[] = [];
  for (const layer of desiredLayers) {
    const merged = mergedByLayerId.get(layer.id);
    if (!merged) continue;
    ordered.push(merged);
    mergedByLayerId.delete(layer.id);
  }
  ordered.push(...mergedByLayerId.values());
  return ordered;
}

function workspaceTemplate(workspace: TickerWorkspace): Template {
  const scenario = workspace.state as unknown as ScenarioContext;
  return {
    id: workspace.id,
    saved_strategy_id: null,
    name: "",
    layers: workspace.strategies
      .slice()
      .sort(
        (a, b) =>
          a.position - b.position ||
          a.created_at.localeCompare(b.created_at) ||
          a.id.localeCompare(b.id),
      )
      .map(strategyLayer),
    scenario: {
      ...scenario,
      draft: false,
      order: workspace.position,
    },
    notes: null,
    tags: [],
    archived_at: workspace.archived_at,
    ticker: workspace.ticker,
    created_at: workspace.created_at,
    updated_at: workspace.updated_at,
  };
}

function normalizeHistoricalWorkspaceCapacity(
  workspace: TickerWorkspace,
  fallbackDte: number,
  holidays: Set<string>,
): {
  workspace: TickerWorkspace;
  disabledStrategies: TickerWorkspaceStrategy[];
} {
  const orderedLayers = workspaceTemplate(workspace).layers;
  const overflowLayerIds = new Set(
    strategySlotCapacityOverflowLayers(
      orderedLayers,
      fallbackDte,
      holidays,
    ).map((layer) => layer.id),
  );
  const normalizedLayers = new Map<string, StrategyLayer>();

  for (const layer of orderedLayers) {
    if (layer.enabled && overflowLayerIds.has(layer.id)) {
      normalizedLayers.set(layer.id, syncLayerEnabled(layer, false));
    }
  }

  if (normalizedLayers.size === 0) {
    return { workspace, disabledStrategies: [] };
  }

  const disabledStrategies: TickerWorkspaceStrategy[] = [];
  const strategies = workspace.strategies.map((strategy) => {
    const layer = normalizedLayers.get(strategy.layer.id);
    if (!layer) return strategy;
    const normalized = { ...strategy, layer, enabled: false };
    disabledStrategies.push(normalized);
    return normalized;
  });

  return {
    workspace: { ...workspace, strategies },
    disabledStrategies,
  };
}

function loadSimulatorWorkspace(
  workspace: TickerWorkspace,
  force = false,
): void {
  const simulator = useSimulatorStore.getState();
  const stateNumber = (key: string): number | null => {
    const value = workspace.state[key];
    return typeof value === "number" && Number.isFinite(value) ? value : null;
  };
  const liveSpot =
    latestPriceFor(useLiveQuotesStore.getState(), workspace.ticker) ??
    stateNumber("evalSpot") ??
    stateNumber("entrySpot") ??
    simulator.evalSpot ??
    simulator.entrySpot;
  const liveIV =
    stateNumber("scenarioIV") ??
    stateNumber("entryIV") ??
    simulator.scenarioIV ??
    simulator.entryIV;
  simulator.loadWorkspace({
    workspaceId: workspace.id,
    workspaceLayers: workspaceTemplate(workspace).layers,
    workspaceState: workspace.state,
    liveSpot,
    liveIV,
    ticker: workspace.ticker,
    force,
  });
}

function isStrategyLayer(value: unknown): value is StrategyLayer {
  return (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    "kind" in value &&
    "params" in value
  );
}

function legacyTabImportTemplate(
  tab: Awaited<
    ReturnType<typeof api.listSimulatorTabs>
  >["simulator_tabs"][number],
): Template {
  return {
    id: tab.id,
    saved_strategy_id: tab.saved_strategy_id,
    name: "",
    layers: tab.layers.filter(isStrategyLayer),
    scenario: {
      ...(tab.scenario ?? {}),
      draft: false,
      order: tab.position,
    },
    notes: null,
    tags: [],
    archived_at: tab.archived_at,
    ticker: tab.ticker,
    created_at: tab.opened_at,
    updated_at: tab.last_active_at,
  };
}

function useTickerWorkspacesProvider(): TickerWorkspacesContextValue {
  const { status: authStatus } = useAuth();
  const location = useLocation();
  const { pathname } = location;
  const [searchParams, setSearchParams] = useSearchParams();
  const onSimulator =
    pathname.startsWith("/workspace/workbench/build") ||
    pathname === "/workspace/v3/workbench";
  const requestedWorkspaceId = searchParams.get("workspace");
  const simulatorPositionId = searchParams.get("position");
  const simulatorSource = searchParams.get("source");
  const [selectedWorkspaceId, setSelectedWorkspaceId] =
    useState(requestedWorkspaceId);
  const selectedWorkspaceIdRef = useRef(selectedWorkspaceId);
  const locationKeyRef = useRef(location.key);
  const pendingWorkspaceUrlRef = useRef<{ target: string | null } | null>(null);
  const legacyTemplateId = searchParams.get("template");
  const [status, setStatus] = useState<TickerWorkspacesStatus>("idle");
  const [workspaces, setWorkspaces] = useState<readonly TickerWorkspace[]>([]);
  const workspacesRef = useRef<readonly TickerWorkspace[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const importingTemplateIds = useRef(new Set<string>());
  const stateWriteChains = useRef(new Map<string, Promise<void>>());
  const layerWriteChains = useRef(new Map<string, Promise<void>>());
  const layerMutationVersions = useRef(new Map<string, number>());
  const persistedStrategies = useRef(
    new Map<
      string,
      { revision: number; strategies: readonly TickerWorkspaceStrategy[] }
    >(),
  );
  const capacityNormalizationWorkspaceIds = useRef(new Set<string>());
  const capacityNormalizationNotifiedWorkspaceIds = useRef(new Set<string>());

  const enqueueWorkspaceStrategyWrite = useCallback(
    <Result,>(
      workspaceId: string,
      operation: () => Promise<Result>,
    ): Promise<Result> => {
      const prior =
        layerWriteChains.current.get(workspaceId) ?? Promise.resolve();
      const request = prior.catch(() => undefined).then(operation);
      const settled = request.then(
        () => undefined,
        () => undefined,
      );
      layerWriteChains.current.set(workspaceId, settled);
      void settled.then(() => {
        if (layerWriteChains.current.get(workspaceId) === settled) {
          layerWriteChains.current.delete(workspaceId);
        }
      });
      return request;
    },
    [],
  );

  useEffect(() => {
    workspacesRef.current = workspaces;
  }, [workspaces]);

  useEffect(() => {
    if (location.key === locationKeyRef.current) return;
    locationKeyRef.current = location.key;
    pendingWorkspaceUrlRef.current = null;
    selectedWorkspaceIdRef.current = requestedWorkspaceId;
    setSelectedWorkspaceId(requestedWorkspaceId);
  }, [location.key, requestedWorkspaceId]);

  useEffect(() => {
    if (!onSimulator || (!simulatorPositionId && !simulatorSource)) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete("position");
        next.delete("source");
        return next;
      },
      { replace: true },
    );
  }, [onSimulator, setSearchParams, simulatorPositionId, simulatorSource]);

  const writeWorkspaceUrl = useCallback(
    (workspaceId: string | null) => {
      pendingWorkspaceUrlRef.current = { target: workspaceId };
      selectedWorkspaceIdRef.current = workspaceId;
      setSelectedWorkspaceId(workspaceId);
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (workspaceId) next.set("workspace", workspaceId);
          else next.delete("workspace");
          next.delete("template");
          next.delete("position");
          next.delete("source");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const reload = useCallback(async () => {
    setStatus("loading");
    try {
      const { ticker_workspaces } = await api.listTickerWorkspaces();
      const nextWorkspaces = sortWorkspaces(ticker_workspaces);
      persistedStrategies.current = new Map(
        nextWorkspaces.map((workspace) => [
          workspace.id,
          {
            revision: workspace.strategies_revision,
            strategies: workspace.strategies,
          },
        ]),
      );
      workspacesRef.current = nextWorkspaces;
      setWorkspaces(nextWorkspaces);
      setStatus("ready");
      setError(null);
    } catch (cause) {
      const nextError =
        cause instanceof Error ? cause : new Error("Ticker workspaces failed");
      setError(nextError);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) void reload();
    });
    return () => {
      cancelled = true;
    };
  }, [authStatus, reload]);

  const activeWorkspace = useMemo(
    () =>
      workspaces.find((workspace) => workspace.id === selectedWorkspaceId) ??
      null,
    [selectedWorkspaceId, workspaces],
  );

  const ensureWorkspace = useCallback(
    async (ticker: string): Promise<TickerWorkspace> => {
      const normalized = ticker.toUpperCase();
      const existing = workspacesRef.current.find(
        (workspace) => workspace.ticker === normalized,
      );
      if (existing) return existing;
      const { ticker_workspace } = await api.createTickerWorkspace({
        ticker: normalized,
      });
      const nextWorkspaces = sortWorkspaces([
        ...workspacesRef.current.filter(
          (workspace) => workspace.id !== ticker_workspace.id,
        ),
        ticker_workspace,
      ]);
      persistedStrategies.current.set(ticker_workspace.id, {
        revision: ticker_workspace.strategies_revision,
        strategies: ticker_workspace.strategies,
      });
      workspacesRef.current = nextWorkspaces;
      setWorkspaces(nextWorkspaces);
      return ticker_workspace;
    },
    [],
  );

  const importTemplate = useCallback(
    async (template: Template): Promise<TickerWorkspaceImportResult> => {
      const normalizedTicker = template.ticker.toUpperCase();
      let workspace = await ensureWorkspace(normalizedTicker);
      return enqueueWorkspaceStrategyWrite(workspace.id, async () => {
        const visibleWorkspace =
          workspacesRef.current.find(
            (candidate) => candidate.id === workspace.id,
          ) ?? workspace;
        const persisted = persistedStrategies.current.get(workspace.id);
        workspace = persisted
          ? {
              ...visibleWorkspace,
              strategies_revision: persisted.revision,
              strategies: persisted.strategies,
            }
          : visibleWorkspace;
        const existingSources = new Set(
          workspace.strategies
            .filter((strategy) =>
              workspaceStrategyBelongsToTemplate(template, strategy),
            )
            .map((strategy) => strategy.source_layer_id),
        );
        const uniformStrategyColor =
          typeof workspace.state.uniformStrategyColor === "string"
            ? workspace.state.uniformStrategyColor
            : null;
        const missingLayers = template.layers
          .filter((layer) => !existingSources.has(layer.id))
          .map((layer) => ({
            ...layer,
            params: { ...layer.params },
            ...(uniformStrategyColor
              ? { color: uniformStrategyColor }
              : undefined),
          }));
        const simulator = useSimulatorStore.getState();
        const prospectiveLayers = [
          ...workspace.strategies.map(strategyLayer),
          ...missingLayers.map((layer) => ({
            ...layer,
            params: { ...layer.params },
          })),
        ];
        const existingLayers = workspace.strategies.map(strategyLayer);
        if (
          increasesStrategySlotOverflow(
            existingLayers,
            prospectiveLayers,
            simulator.dte,
            simulator.holidays,
          )
        ) {
          const overflowLayers = strategySlotCapacityOverflowLayers(
            prospectiveLayers,
            simulator.dte,
            simulator.holidays,
          );
          emit("strategy:limit-reached", {
            layers: overflowLayers,
            resolution: "remove",
          });
          return { workspace, limitReached: true };
        }

        for (let index = 0; index < missingLayers.length; index += 1) {
          const layer = missingLayers[index];
          const enabled = layer.enabled;
          let ticker_workspace_strategies: TickerWorkspaceStrategy[];
          let strategies_revision: number;
          try {
            ({ ticker_workspace_strategies, strategies_revision } =
              await api.createTickerWorkspaceStrategy(workspace.id, {
                layer,
                enabled,
                position: workspace.strategies.length,
                simulator_tab_id: simulatorTabIdForTemplate(template),
                saved_strategy_id: template.saved_strategy_id,
                source_layer_id: layer.id,
              }));
          } catch (error) {
            const body =
              error instanceof ApiError
                ? (error.body as { error?: unknown })
                : null;
            if (
              body?.error === "strategy_limit" ||
              body?.error === "enabled_strategy_limit"
            ) {
              emit("strategy:limit-reached", {
                layers: missingLayers.slice(index),
              });
              await reload();
              return { workspace, limitReached: true };
            }
            throw error;
          }
          workspace = {
            ...workspace,
            strategies_revision,
            strategies: ticker_workspace_strategies,
          };
        }

        persistedStrategies.current.set(workspace.id, {
          revision: workspace.strategies_revision,
          strategies: workspace.strategies,
        });
        workspacesRef.current = sortWorkspaces(
          workspacesRef.current.map((candidate) =>
            candidate.id === workspace.id ? workspace : candidate,
          ),
        );

        setWorkspaces(workspacesRef.current);
        loadSimulatorWorkspace(workspace, true);
        return { workspace, limitReached: false };
      });
    },
    [enqueueWorkspaceStrategyWrite, ensureWorkspace, reload],
  );

  // Backward-compatible entry seam: a stale external link may still arrive
  // with a legacy simulator-tab id. Read that record directly from the API,
  // import it exactly once, then replace the URL with the independent
  // workspace id. The legacy tab provider is deliberately not mounted on the
  // Strategy Center route.
  useEffect(() => {
    if (!onSimulator || status !== "ready" || !legacyTemplateId) return;
    if (importingTemplateIds.current.has(legacyTemplateId)) return;
    importingTemplateIds.current.add(legacyTemplateId);
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      void api
        .listSimulatorTabs()
        .then(({ simulator_tabs }) =>
          simulator_tabs.find((tab) => tab.id === legacyTemplateId),
        )
        .then((tab) =>
          tab ? importTemplate(legacyTabImportTemplate(tab)) : null,
        )
        .then((result) => {
          if (result) writeWorkspaceUrl(result.workspace.id);
          else if (activeWorkspace) writeWorkspaceUrl(activeWorkspace.id);
        })
        .finally(() => importingTemplateIds.current.delete(legacyTemplateId));
    });
    return () => {
      cancelled = true;
    };
  }, [
    importTemplate,
    activeWorkspace,
    legacyTemplateId,
    onSimulator,
    status,
    writeWorkspaceUrl,
  ]);

  // Direct simulator visits resolve to a real workspace. This does not seed or
  // activate any legacy simulator tab.
  useEffect(() => {
    if (!onSimulator || status !== "ready" || legacyTemplateId) return;
    const pendingWorkspaceUrl = pendingWorkspaceUrlRef.current;
    if (
      pendingWorkspaceUrl &&
      requestedWorkspaceId !== pendingWorkspaceUrl.target
    ) {
      return;
    }
    const requestedWorkspace = requestedWorkspaceId
      ? (workspaces.find(
          (workspace) => workspace.id === requestedWorkspaceId,
        ) ?? null)
      : null;
    if (requestedWorkspace && activeWorkspace?.id === requestedWorkspace.id) {
      return;
    }
    const target =
      requestedWorkspace ??
      workspaces
        .slice()
        .sort((a, b) => b.last_active_at.localeCompare(a.last_active_at))[0] ??
      null;
    if (target) {
      const targetId = target.id;
      queueMicrotask(() => writeWorkspaceUrl(targetId));
      return;
    }
    useSimulatorStore.getState().clearWorkspace();
    if (requestedWorkspaceId) {
      queueMicrotask(() => writeWorkspaceUrl(null));
    }
  }, [
    activeWorkspace,
    legacyTemplateId,
    onSimulator,
    requestedWorkspaceId,
    status,
    workspaces,
    writeWorkspaceUrl,
  ]);

  useEffect(() => {
    if (!onSimulator || status !== "ready" || !activeWorkspace) return;
    const simulator = useSimulatorStore.getState();
    const normalized = normalizeHistoricalWorkspaceCapacity(
      activeWorkspace,
      simulator.dte,
      simulator.holidays,
    );
    if (normalized.disabledStrategies.length > 0) {
      emit("autosave:cancel");
    }
    loadSimulatorWorkspace(
      normalized.workspace,
      normalized.disabledStrategies.length > 0,
    );
    if (normalized.disabledStrategies.length === 0) return;

    const workspaceId = activeWorkspace.id;
    const replaceWorkspace = (
      current: readonly TickerWorkspace[],
      nextWorkspace: TickerWorkspace,
    ) =>
      current.map((workspace) =>
        workspace.id === workspaceId ? nextWorkspace : workspace,
      );
    workspacesRef.current = replaceWorkspace(
      workspacesRef.current,
      normalized.workspace,
    );
    queueMicrotask(() =>
      setWorkspaces((current) =>
        replaceWorkspace(current, normalized.workspace),
      ),
    );

    if (!capacityNormalizationNotifiedWorkspaceIds.current.has(workspaceId)) {
      capacityNormalizationNotifiedWorkspaceIds.current.add(workspaceId);
      window.setTimeout(
        () =>
          emit("strategy:historical-overflow-disabled", {
            count: normalized.disabledStrategies.length,
          }),
        0,
      );
    }
    if (capacityNormalizationWorkspaceIds.current.has(workspaceId)) return;
    capacityNormalizationWorkspaceIds.current.add(workspaceId);

    void enqueueWorkspaceStrategyWrite(workspaceId, async () => {
      let canonicalStrategies = normalized.workspace.strategies;
      let persistedRevision = activeWorkspace.strategies_revision;
      for (const strategy of normalized.disabledStrategies) {
        const { ticker_workspace_strategies, strategies_revision } =
          await api.updateTickerWorkspaceStrategy(workspaceId, strategy.id, {
            layer: strategy.layer,
            enabled: false,
            position: strategy.position,
          });
        canonicalStrategies = ticker_workspace_strategies;
        persistedRevision = strategies_revision;
      }
      return { canonicalStrategies, persistedRevision };
    })
      .then(({ canonicalStrategies, persistedRevision }) => {
        const mergePersisted = (current: readonly TickerWorkspace[]) =>
          current.map((workspace) =>
            workspace.id === workspaceId
              ? {
                  ...workspace,
                  strategies_revision: persistedRevision,
                  strategies: canonicalStrategies,
                }
              : workspace,
          );
        workspacesRef.current = mergePersisted(workspacesRef.current);
        const persistedWorkspace = workspacesRef.current.find(
          (workspace) => workspace.id === workspaceId,
        );
        if (persistedWorkspace) {
          persistedStrategies.current.set(workspaceId, {
            revision: persistedWorkspace.strategies_revision,
            strategies: persistedWorkspace.strategies,
          });
        }
        setWorkspaces(mergePersisted);
      })
      .catch(() => {
        capacityNormalizationWorkspaceIds.current.delete(workspaceId);
        void reload();
      });
  }, [
    activeWorkspace,
    enqueueWorkspaceStrategyWrite,
    onSimulator,
    reload,
    status,
  ]);

  const switchWorkspace = useCallback(
    (workspaceId: string) => {
      const target = workspacesRef.current.find(
        (workspace) => workspace.id === workspaceId,
      );
      if (target) loadSimulatorWorkspace(target);
      if (workspaceId === selectedWorkspaceIdRef.current) return;
      writeWorkspaceUrl(workspaceId);
      // Touch last_active_at on the server without replacing the local
      // workspace object. Reconciliation here caused a second full simulator
      // render after every switch even though no visible workspace data
      // changed.
      void api.updateTickerWorkspace(workspaceId, {});
    },
    [writeWorkspaceUrl],
  );

  const archiveWorkspace = useCallback(
    async (workspaceId: string) => {
      await api.archiveTickerWorkspace(workspaceId);
      const remaining = workspacesRef.current.filter(
        (workspace) => workspace.id !== workspaceId,
      );
      persistedStrategies.current.delete(workspaceId);
      layerMutationVersions.current.delete(workspaceId);
      layerWriteChains.current.delete(workspaceId);
      workspacesRef.current = remaining;
      setWorkspaces(remaining);
      if (selectedWorkspaceIdRef.current !== workspaceId) return;

      const nextWorkspace =
        remaining.find((workspace) => workspace.strategies.length > 0) ??
        remaining[0] ??
        null;
      if (nextWorkspace) {
        switchWorkspace(nextWorkspace.id);
      } else {
        useSimulatorStore.getState().clearWorkspace();
        writeWorkspaceUrl(null);
      }
    },
    [switchWorkspace, writeWorkspaceUrl],
  );

  const updateWorkspaceState = useCallback(
    async (workspaceId: string, patch: Record<string, unknown>) => {
      const currentWorkspace = workspacesRef.current.find(
        (workspace) => workspace.id === workspaceId,
      );
      if (!currentWorkspace) return;
      const nextState = { ...currentWorkspace.state, ...patch };
      workspacesRef.current = workspacesRef.current.map((workspace) =>
        workspace.id === workspaceId
          ? { ...workspace, state: nextState }
          : workspace,
      );
      setWorkspaces((current) =>
        current.map((workspace) => {
          if (workspace.id !== workspaceId) return workspace;
          return { ...workspace, state: nextState };
        }),
      );
      const prior =
        stateWriteChains.current.get(workspaceId) ?? Promise.resolve();
      const request = prior
        .catch(() => undefined)
        .then(async () => {
          const { ticker_workspace } = await api.updateTickerWorkspace(
            workspaceId,
            { state: nextState },
          );
          const mergeResponse = (current: readonly TickerWorkspace[]) =>
            current.map((workspace) => {
              if (workspace.id !== workspaceId) return workspace;
              return {
                ...workspace,
                ...(deepEqual(workspace.state, nextState)
                  ? { state: ticker_workspace.state }
                  : undefined),
                strategies_revision: Math.max(
                  workspace.strategies_revision,
                  ticker_workspace.strategies_revision,
                ),
                last_active_at: ticker_workspace.last_active_at,
                updated_at: ticker_workspace.updated_at,
              };
            });
          // State writes must never replace the strategy array. They may race
          // a newer optimistic layer mutation, and the workspace response was
          // serialized before that mutation completed.
          workspacesRef.current = mergeResponse(workspacesRef.current);
          setWorkspaces(mergeResponse);
        })
        .catch(async (cause) => {
          await reload();
          throw cause;
        });
      stateWriteChains.current.set(workspaceId, request);
      await request;
    },
    [reload],
  );

  const persistWorkspaceLayers = useCallback(
    async (workspaceId: string, layers: readonly StrategyLayer[]) => {
      const workspace = workspacesRef.current.find(
        (candidate) => candidate.id === workspaceId,
      );
      if (!workspace) return;

      const desiredLayers = layers.map((layer) => ({
        ...layer,
        params: { ...layer.params },
      }));
      const intentBaseStrategies = workspace.strategies;
      const intentBaseRevision =
        persistedStrategies.current.get(workspaceId)?.revision ??
        workspace.strategies_revision;
      const mutationVersion =
        (layerMutationVersions.current.get(workspaceId) ?? 0) + 1;
      layerMutationVersions.current.set(workspaceId, mutationVersion);

      const applyOptimistic = (current: readonly TickerWorkspace[]) =>
        current.map((candidate) =>
          candidate.id === workspaceId
            ? {
                ...candidate,
                strategies: optimisticWorkspaceStrategies(
                  candidate,
                  desiredLayers,
                ),
              }
            : candidate,
        );
      workspacesRef.current = applyOptimistic(workspacesRef.current);
      setWorkspaces(applyOptimistic);

      const request = enqueueWorkspaceStrategyWrite(workspaceId, async () => {
        let response:
          | Awaited<ReturnType<typeof api.replaceTickerWorkspaceStrategies>>
          | undefined;

        let attemptLayers = desiredLayers;
        for (let attempt = 0; attempt < 2; attempt += 1) {
          const server = persistedStrategies.current.get(workspaceId) ?? {
            revision: workspace.strategies_revision,
            strategies: workspace.strategies.filter(
              (strategy) => !strategy.id.startsWith("pending:"),
            ),
          };
          if (attempt === 0 && server.revision !== intentBaseRevision) {
            attemptLayers = rebaseWorkspaceLayers(
              intentBaseStrategies,
              attemptLayers,
              server.strategies,
            );
          }
          const serverByLayerId = new Map(
            server.strategies.map((strategy) => [strategy.layer.id, strategy]),
          );
          const replacements = attemptLayers.map((layer) => {
            const existing = serverByLayerId.get(layer.id);
            return {
              ...(existing ? { id: existing.id } : undefined),
              layer,
              enabled: layer.enabled,
            };
          });

          try {
            response = await api.replaceTickerWorkspaceStrategies(
              workspaceId,
              server.revision,
              replacements,
            );
            break;
          } catch (error) {
            const body =
              error instanceof ApiError && error.status === 409
                ? (error.body as {
                    strategies_revision?: unknown;
                    ticker_workspace_strategies?: unknown;
                  })
                : null;
            if (
              attempt === 0 &&
              typeof body?.strategies_revision === "number" &&
              Array.isArray(body.ticker_workspace_strategies)
            ) {
              const canonicalStrategies =
                body.ticker_workspace_strategies as TickerWorkspaceStrategy[];
              attemptLayers = rebaseWorkspaceLayers(
                server.strategies,
                attemptLayers,
                canonicalStrategies,
              );
              persistedStrategies.current.set(workspaceId, {
                revision: body.strategies_revision,
                strategies: canonicalStrategies,
              });
              continue;
            }
            throw error;
          }
        }

        if (!response) throw new Error("Workspace strategy sync failed");
        persistedStrategies.current.set(workspaceId, {
          revision: response.strategies_revision,
          strategies: response.ticker_workspace_strategies,
        });

        if (
          layerMutationVersions.current.get(workspaceId) !== mutationVersion
        ) {
          return;
        }
        const mergeCanonical = (current: readonly TickerWorkspace[]) =>
          current.map((candidate) =>
            candidate.id === workspaceId
              ? {
                  ...candidate,
                  strategies_revision: response.strategies_revision,
                  strategies: response.ticker_workspace_strategies,
                }
              : candidate,
          );
        workspacesRef.current = mergeCanonical(workspacesRef.current);
        setWorkspaces(mergeCanonical);
      }).catch(async (cause) => {
        if (
          layerMutationVersions.current.get(workspaceId) !== mutationVersion
        ) {
          return;
        }
        await reload();
        throw cause;
      });
      await request;
    },
    [enqueueWorkspaceStrategyWrite, reload],
  );

  const replaceWorkspaceLayers = useCallback(
    async (workspaceId: string, layers: StrategyLayer[]) => {
      const workspace = workspacesRef.current.find(
        (candidate) => candidate.id === workspaceId,
      );
      if (!workspace) return;
      const simulator = useSimulatorStore.getState();
      const previousLayers = workspace.strategies.map(strategyLayer);
      if (
        increasesStrategySlotOverflow(
          previousLayers,
          layers,
          simulator.dte,
          simulator.holidays,
        )
      ) {
        emit("strategy:limit-reached", {
          layers: strategySlotCapacityOverflowLayers(
            layers,
            simulator.dte,
            simulator.holidays,
          ),
          resolution: "remove",
        });
        return;
      }
      const overflowLayers = simulationCapacityOverflowLayers(
        layers,
        simulator.dte,
        simulator.holidays,
      );
      if (overflowLayers.length > 0) {
        emit("strategy:limit-reached", { layers: overflowLayers });
        return;
      }
      await persistWorkspaceLayers(workspaceId, layers);
    },
    [persistWorkspaceLayers],
  );

  const saveWorkspaceLayer = useCallback(
    async (workspaceId: string, layer: StrategyLayer) => {
      const workspace = workspacesRef.current.find(
        (item) => item.id === workspaceId,
      );
      if (!workspace) throw new Error("Workspace unavailable");
      const previous = workspace.strategies.map(strategyLayer);
      const exists = previous.some((item) => item.id === layer.id);
      const layers = exists
        ? previous.map((item) => (item.id === layer.id ? layer : item))
        : [...previous, layer];
      const simulator = useSimulatorStore.getState();
      if (
        increasesStrategySlotOverflow(
          previous,
          layers,
          simulator.dte,
          simulator.holidays,
        ) ||
        simulationCapacityOverflowLayers(
          layers,
          simulator.dte,
          simulator.holidays,
        ).length
      ) {
        throw new Error("Strategy capacity exceeded");
      }
      await persistWorkspaceLayers(workspaceId, layers);
    },
    [persistWorkspaceLayers],
  );

  const removeWorkspaceLayer = useCallback(
    async (workspaceId: string, layerId: string) => {
      const workspace = workspacesRef.current.find(
        (candidate) => candidate.id === workspaceId,
      );
      if (!workspace) return;
      const remainingLayers = workspace.strategies
        .filter((strategy) => strategy.layer.id !== layerId)
        .map(strategyLayer);
      if (remainingLayers.length === workspace.strategies.length) return;

      emit("autosave:cancel");
      await persistWorkspaceLayers(workspaceId, remainingLayers);
    },
    [persistWorkspaceLayers],
  );

  const removeWorkspaceLayerWithHistory = useCallback(
    async (workspaceId: string, layerId: string) => {
      emit("autosave:cancel");
      await enqueueWorkspaceStrategyWrite(workspaceId, async () => {
        const workspace = workspacesRef.current.find(
          (candidate) => candidate.id === workspaceId,
        );
        const strategy = workspace?.strategies.find(
          (candidate) => candidate.layer.id === layerId,
        );
        if (!strategy) return;
        const response = await api.deleteTickerWorkspaceStrategy(
          workspaceId,
          strategy.id,
          true,
        );
        persistedStrategies.current.set(workspaceId, {
          revision: response.strategies_revision,
          strategies: response.ticker_workspace_strategies,
        });
        const merge = (current: readonly TickerWorkspace[]) =>
          current.map((candidate) =>
            candidate.id === workspaceId
              ? {
                  ...candidate,
                  strategies_revision: response.strategies_revision,
                  strategies: response.ticker_workspace_strategies,
                }
              : candidate,
          );
        workspacesRef.current = merge(workspacesRef.current);
        setWorkspaces(merge);
      });
    },
    [enqueueWorkspaceStrategyWrite],
  );

  const activeTemplate = useMemo(
    () => (activeWorkspace ? workspaceTemplate(activeWorkspace) : undefined),
    [activeWorkspace],
  );

  return useMemo(
    () => ({
      status,
      workspaces,
      activeWorkspaceId: selectedWorkspaceId,
      activeWorkspace,
      activeTemplate,
      error,
      reload,
      switchWorkspace,
      ensureWorkspace,
      archiveWorkspace,
      importTemplate,
      updateWorkspaceState,
      replaceWorkspaceLayers,
      removeWorkspaceLayer,
      removeWorkspaceLayerWithHistory,
      saveWorkspaceLayer,
    }),
    [
      activeTemplate,
      activeWorkspace,
      archiveWorkspace,
      ensureWorkspace,
      error,
      importTemplate,
      reload,
      removeWorkspaceLayer,
      removeWorkspaceLayerWithHistory,
      saveWorkspaceLayer,
      replaceWorkspaceLayers,
      selectedWorkspaceId,
      status,
      switchWorkspace,
      updateWorkspaceState,
      workspaces,
    ],
  );
}

export function TickerWorkspacesProvider({
  children,
}: {
  children: ReactNode;
}) {
  const value = useTickerWorkspacesProvider();
  return (
    <TickerWorkspacesContext.Provider value={value}>
      {children}
    </TickerWorkspacesContext.Provider>
  );
}

export function useTickerWorkspaces(): TickerWorkspacesContextValue {
  const context = useContext(TickerWorkspacesContext);
  if (!context) {
    throw new Error(
      "useTickerWorkspaces must be used within a TickerWorkspacesProvider",
    );
  }
  return context;
}

export function useOptionalTickerWorkspaces(): TickerWorkspacesContextValue | null {
  return useContext(TickerWorkspacesContext);
}
